import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Cpu, Atom, Zap, Shield, Rocket, ChevronRight, Sparkles } from 'lucide-react';
import { useTheme } from '../../contexts/Themecontext';

const Home = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [scrollY, setScrollY] = useState(0);
  const { theme } = useTheme();
  const heroRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ 
        x: e.clientX / window.innerWidth, 
        y: e.clientY / window.innerHeight 
      });
    };

    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const features = [
    {
      icon: <Cpu className="w-8 h-8" />,
      title: 'Semiconductor Excellence',
      description: 'Advanced chip design with nanoscale precision for next-generation computing architectures',
      link: '/semiconductor',
    },
    {
      icon: <Atom className="w-8 h-8" />,
      title: 'Quantum Computing',
      description: 'Breakthrough quantum algorithms leveraging superposition and entanglement principles',
      link: '/research',
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: 'Superconducting Systems',
      description: 'Zero-resistance materials enabling ultra-efficient quantum circuit integration',
      link: '/superconductor',
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Secure Computing',
      description: 'Quantum-resistant cryptography protecting against future computational threats',
      link: '/ai',
    },
  ];

  const stats = [
    { value: '50+', label: 'Research Projects', icon: <Rocket className="w-6 h-6" /> },
    { value: '100+', label: 'Team Members', icon: <Cpu className="w-6 h-6" /> },
    { value: '25+', label: 'Partner Institutions', icon: <Atom className="w-6 h-6" /> },
    { value: '10+', label: 'Patents Filed', icon: <Sparkles className="w-6 h-6" /> },
  ];

  const getThemeClasses = () => {
    if (theme === 'light') {
      return {
        heroBg: 'bg-gradient-to-br from-blue-50 via-white to-purple-50',
        heroText: 'text-gray-900',
        heroSubtext: 'text-gray-600',
        cardBg: 'bg-white',
        cardBorder: 'border-gray-200',
        cardText: 'text-gray-900',
        cardSubtext: 'text-gray-600',
        cardHover: 'hover:border-blue-300 hover:shadow-blue-100',
        particleColor: 'bg-blue-400',
        accentColor: 'blue',
      };
    }
    if (theme === 'dark') {
      return {
        heroBg: 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900',
        heroText: 'text-white',
        heroSubtext: 'text-gray-300',
        cardBg: 'bg-slate-800',
        cardBorder: 'border-slate-700',
        cardText: 'text-white',
        cardSubtext: 'text-gray-400',
        cardHover: 'hover:border-cyan-500 hover:shadow-cyan-900',
        particleColor: 'bg-cyan-400',
        accentColor: 'cyan',
      };
    }
    return {
      heroBg: 'bg-gradient-to-br from-slate-950 via-indigo-950 to-purple-950',
      heroText: 'text-white',
      heroSubtext: 'text-gray-200',
      cardBg: 'bg-gradient-to-br from-slate-900/50 to-purple-900/50',
      cardBorder: 'border-purple-500/30',
      cardText: 'text-white',
      cardSubtext: 'text-gray-300',
      cardHover: 'hover:border-purple-400 hover:shadow-purple-900',
      particleColor: 'bg-purple-400',
      accentColor: 'purple',
    };
  };

  const classes = getThemeClasses();

  return (
    <div className="min-h-screen overflow-hidden">
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          25% { transform: translateY(-20px) translateX(10px); }
          50% { transform: translateY(-10px) translateX(-10px); }
          75% { transform: translateY(-15px) translateX(5px); }
        }

        @keyframes pulse-glow {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.05); }
        }

        @keyframes orbit-1 {
          from { transform: rotate(0deg) translateX(120px) rotate(0deg); }
          to { transform: rotate(360deg) translateX(120px) rotate(-360deg); }
        }

        @keyframes orbit-2 {
          from { transform: rotate(120deg) translateX(140px) rotate(-120deg); }
          to { transform: rotate(480deg) translateX(140px) rotate(-480deg); }
        }

        @keyframes orbit-3 {
          from { transform: rotate(240deg) translateX(100px) rotate(-240deg); }
          to { transform: rotate(600deg) translateX(100px) rotate(-600deg); }
        }

        @keyframes rotate-sphere {
          from { transform: rotateY(0deg) rotateX(20deg); }
          to { transform: rotateY(360deg) rotateX(20deg); }
        }

        @keyframes wave-flow {
          0% { transform: translateX(-100%) translateY(0); }
          100% { transform: translateX(100%) translateY(0); }
        }

        @keyframes electron-orbit {
          from { transform: rotateZ(0deg); }
          to { transform: rotateZ(360deg); }
        }

        @keyframes particle-drift {
          0% { transform: translate(0, 0) scale(1); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translate(var(--tx), var(--ty)) scale(0.5); opacity: 0; }
        }

        @keyframes data-stream {
          0% { transform: translateY(0) scale(1); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 0.8; }
          100% { transform: translateY(-100vh) scale(0.8); opacity: 0; }
        }

        @keyframes circuit-pulse {
          0%, 100% { stroke-dashoffset: 1000; opacity: 0.3; }
          50% { stroke-dashoffset: 0; opacity: 1; }
        }

        @keyframes magnetic-field {
          0%, 100% { transform: scale(1) rotate(0deg); opacity: 0.3; }
          50% { transform: scale(1.2) rotate(180deg); opacity: 0.6; }
        }

        @keyframes quantum-fluctuation {
          0%, 100% { transform: translate(0, 0); filter: blur(0px); }
          25% { transform: translate(5px, -5px); filter: blur(1px); }
          50% { transform: translate(-3px, 3px); filter: blur(0.5px); }
          75% { transform: translate(3px, 2px); filter: blur(1px); }
        }

        @keyframes slide-in-bottom {
          from { transform: translateY(100px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }

        @keyframes slide-in-left {
          from { transform: translateX(-100px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        @keyframes slide-in-right {
          from { transform: translateX(100px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes scale-in {
          from { transform: scale(0.8); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }

        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }

        @keyframes energy-wave {
          0% { transform: scale(0.8); opacity: 0; }
          50% { opacity: 0.5; }
          100% { transform: scale(2); opacity: 0; }
        }

        .animate-float {
          animation: float 6s ease-in-out infinite;
        }

        .animate-pulse-glow {
          animation: pulse-glow 3s ease-in-out infinite;
        }

        .animate-orbit-1 {
          animation: orbit-1 20s linear infinite;
        }

        .animate-orbit-2 {
          animation: orbit-2 25s linear infinite;
        }

        .animate-orbit-3 {
          animation: orbit-3 30s linear infinite;
        }

        .animate-rotate-sphere {
          animation: rotate-sphere 20s linear infinite;
        }

        .animate-wave-flow {
          animation: wave-flow 3s linear infinite;
        }

        .animate-electron-orbit {
          animation: electron-orbit 10s linear infinite;
        }

        .animate-particle-drift {
          animation: particle-drift 8s ease-in-out infinite;
        }

        .animate-data-stream {
          animation: data-stream 4s linear infinite;
        }

        .animate-circuit-pulse {
          animation: circuit-pulse 3s ease-in-out infinite;
        }

        .animate-magnetic-field {
          animation: magnetic-field 6s ease-in-out infinite;
        }

        .animate-quantum-fluctuation {
          animation: quantum-fluctuation 2s ease-in-out infinite;
        }

        .animate-slide-in-bottom {
          animation: slide-in-bottom 0.8s ease-out forwards;
        }

        .animate-slide-in-left {
          animation: slide-in-left 0.8s ease-out forwards;
        }

        .animate-slide-in-right {
          animation: slide-in-right 0.8s ease-out forwards;
        }

        .animate-fade-in {
          animation: fade-in 1s ease-out forwards;
        }

        .animate-scale-in {
          animation: scale-in 0.6s ease-out forwards;
        }

        .animate-shimmer {
          animation: shimmer 3s linear infinite;
        }

        .animate-energy-wave {
          animation: energy-wave 2s ease-out infinite;
        }

        .perspective-3d {
          perspective: 1000px;
        }

        .transform-3d {
          transform-style: preserve-3d;
        }

        .card-3d {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .card-3d:hover {
          transform: translateY(-10px) rotateX(5deg) rotateY(5deg);
        }

        .glass-effect {
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
        }

        .gradient-shimmer {
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.1),
            transparent
          );
          background-size: 200% 100%;
        }
      `}</style>

      {/* Hero Section */}
      <section
        ref={heroRef}
        className={`relative min-h-screen flex items-center justify-center ${classes.heroBg} overflow-hidden`}
        style={{
          transform: `translateY(${scrollY * 0.5}px)`,
        }}
      >
        {/* Quantum Particle Field */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(100)].map((_, i) => {
            const randomX = Math.random() * 100;
            const randomY = Math.random() * 100;
            const randomDelay = Math.random() * 8;
            const randomDuration = 8 + Math.random() * 4;
            const randomTx = (Math.random() - 0.5) * 200;
            const randomTy = (Math.random() - 0.5) * 200;
            
            return (
              <div
                key={`particle-${i}`}
                className={`absolute w-1 h-1 ${classes.particleColor} rounded-full`}
                style={{
                  left: `${randomX}%`,
                  top: `${randomY}%`,
                  animation: `particle-drift ${randomDuration}s ease-in-out ${randomDelay}s infinite`,
                  '--tx': `${randomTx}px`,
                  '--ty': `${randomTy}px`,
                  opacity: 0.6,
                }}
              />
            );
          })}
        </div>

        {/* Superconductor Magnetic Field Lines */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="field-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={theme === 'light' ? '#3b82f6' : theme === 'dark' ? '#06b6d4' : '#a855f7'} stopOpacity="0.3" />
                <stop offset="100%" stopColor={theme === 'light' ? '#8b5cf6' : theme === 'dark' ? '#3b82f6' : '#ec4899'} stopOpacity="0" />
              </linearGradient>
            </defs>
            {[...Array(8)].map((_, i) => {
              const y = 10 + i * 12;
              return (
                <path
                  key={`field-${i}`}
                  d={`M 0 ${y}% Q 25 ${y - 5}%, 50 ${y}% T 100 ${y}%`}
                  stroke="url(#field-gradient)"
                  strokeWidth="2"
                  fill="none"
                  className="animate-wave-flow"
                  style={{ animationDelay: `${i * 0.3}s` }}
                />
              );
            })}
          </svg>
        </div>

        {/* Data Streams (Semiconductor Logic Gates) */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => {
            const randomX = Math.random() * 100;
            const randomDelay = Math.random() * 4;
            const randomDuration = 4 + Math.random() * 2;
            
            return (
              <div
                key={`stream-${i}`}
                className={`absolute w-px h-16 ${classes.particleColor} opacity-40`}
                style={{
                  left: `${randomX}%`,
                  bottom: '0',
                  animation: `data-stream ${randomDuration}s linear ${randomDelay}s infinite`,
                  boxShadow: `0 0 10px ${theme === 'light' ? '#3b82f6' : theme === 'dark' ? '#06b6d4' : '#a855f7'}`,
                }}
              />
            );
          })}
        </div>

        {/* Interactive Gradient Follower */}
        {theme === 'quantum' && (
          <div
            className="absolute pointer-events-none"
            style={{
              width: '600px',
              height: '600px',
              left: `${mousePosition.x * 100}%`,
              top: `${mousePosition.y * 100}%`,
              transform: 'translate(-50%, -50%)',
              background: 'radial-gradient(circle, rgba(168, 85, 247, 0.15) 0%, transparent 70%)',
              transition: 'left 0.3s ease, top 0.3s ease',
              filter: 'blur(40px)',
            }}
          />
        )}

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Hero Text */}
            <div className="text-center lg:text-left space-y-6 sm:space-y-8 animate-slide-in-left">
              <div className="inline-block">
                <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${
                  theme === 'light' 
                    ? 'bg-blue-100 text-blue-700' 
                    : theme === 'dark'
                    ? 'bg-cyan-900/30 text-cyan-400'
                    : 'bg-purple-900/30 text-purple-400'
                } backdrop-blur-sm border ${classes.cardBorder} animate-pulse-glow`}>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Next-Gen Quantum Technology
                </span>
              </div>

              <h1 className={`text-4xl sm:text-5xl lg:text-7xl font-bold ${classes.heroText} leading-tight`}>
                Pioneering the <br />
                <span className={`bg-gradient-to-r ${
                  theme === 'light'
                    ? 'from-blue-600 to-purple-600'
                    : theme === 'dark'
                    ? 'from-cyan-400 to-blue-400'
                    : 'from-purple-400 to-pink-400'
                } bg-clip-text text-transparent animate-shimmer gradient-shimmer`}>
                  Quantum Revolution
                </span>
              </h1>

              <p className={`text-base sm:text-lg lg:text-xl ${classes.heroSubtext} max-w-2xl mx-auto lg:mx-0`}>
                GLYQIX merges semiconductor precision, superconducting materials, and quantum computing to architect 
                intelligent, secure, and sustainable computational platforms for tomorrow's challenges.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link
                  to="/get-started"
                  className={`group inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 rounded-xl text-white font-semibold ${
                    theme === 'light'
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
                      : theme === 'dark'
                      ? 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700'
                      : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
                  } transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1`}
                >
                  Get Started
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/research"
                  className={`group inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold border-2 ${
                    theme === 'light'
                      ? 'border-blue-600 text-blue-600 hover:bg-blue-50'
                      : theme === 'dark'
                      ? 'border-cyan-500 text-cyan-400 hover:bg-cyan-950'
                      : 'border-purple-500 text-purple-400 hover:bg-purple-950'
                  } transition-all duration-300 backdrop-blur-sm`}
                >
                  Explore Research
                  <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-4 pt-4 sm:pt-8">
                <div className="text-center">
                  <div className={`text-2xl sm:text-3xl font-bold ${classes.heroText}`}>99.9%</div>
                  <div className={`text-xs sm:text-sm ${classes.heroSubtext}`}>Uptime</div>
                </div>
                <div className="text-center">
                  <div className={`text-2xl sm:text-3xl font-bold ${classes.heroText}`}>10ms</div>
                  <div className={`text-xs sm:text-sm ${classes.heroSubtext}`}>Latency</div>
                </div>
                <div className="text-center">
                  <div className={`text-2xl sm:text-3xl font-bold ${classes.heroText}`}>256-bit</div>
                  <div className={`text-xs sm:text-sm ${classes.heroSubtext}`}>Encryption</div>
                </div>
              </div>
            </div>

            {/* 3D Quantum Visualization */}
            <div className="relative h-64 sm:h-96 lg:h-[500px] flex items-center justify-center perspective-3d animate-slide-in-right">
              {/* Quantum Sphere Container */}
              <div className="relative w-full h-full flex items-center justify-center transform-3d">
                {/* Core Quantum Sphere */}
                <div className="relative animate-rotate-sphere transform-3d">
                  {/* Inner Core */}
                  <div className={`absolute w-32 h-32 sm:w-40 sm:h-40 lg:w-48 lg:h-48 rounded-full ${
                    theme === 'light'
                      ? 'bg-gradient-to-br from-blue-500 to-purple-500'
                      : theme === 'dark'
                      ? 'bg-gradient-to-br from-cyan-500 to-blue-500'
                      : 'bg-gradient-to-br from-purple-500 to-pink-500'
                  } blur-xl opacity-60 animate-pulse-glow`}
                    style={{ transform: 'translate(-50%, -50%)', left: '50%', top: '50%' }}
                  />
                  
                  {/* Solid Core */}
                  <div className={`absolute w-24 h-24 sm:w-32 sm:h-32 lg:w-40 lg:h-40 rounded-full ${
                    theme === 'light'
                      ? 'bg-gradient-to-br from-blue-600 to-purple-600'
                      : theme === 'dark'
                      ? 'bg-gradient-to-br from-cyan-600 to-blue-600'
                      : 'bg-gradient-to-br from-purple-600 to-pink-600'
                  } shadow-2xl animate-quantum-fluctuation glass-effect`}
                    style={{ transform: 'translate(-50%, -50%)', left: '50%', top: '50%' }}
                  >
                    <div className="absolute inset-0 rounded-full bg-white opacity-20 animate-energy-wave" />
                  </div>
                </div>

                {/* Orbital Rings (Electron Shells) */}
                <div className="absolute inset-0 flex items-center justify-center">
                  {/* Ring 1 */}
                  <div
                    className={`absolute w-48 h-48 sm:w-64 sm:h-64 lg:w-80 lg:h-80 border-2 ${
                      theme === 'light' ? 'border-blue-400' : theme === 'dark' ? 'border-cyan-400' : 'border-purple-400'
                    } rounded-full opacity-30 animate-electron-orbit`}
                    style={{ animationDuration: '10s' }}
                  >
                    <div className={`absolute w-3 h-3 sm:w-4 sm:h-4 ${classes.particleColor} rounded-full top-0 left-1/2 -ml-1.5 sm:-ml-2 shadow-lg`} />
                  </div>

                  {/* Ring 2 */}
                  <div
                    className={`absolute w-56 h-56 sm:w-72 sm:h-72 lg:w-96 lg:h-96 border-2 ${
                      theme === 'light' ? 'border-purple-400' : theme === 'dark' ? 'border-blue-400' : 'border-pink-400'
                    } rounded-full opacity-20 animate-electron-orbit`}
                    style={{ animationDuration: '15s', animationDirection: 'reverse' }}
                  >
                    <div className={`absolute w-3 h-3 sm:w-4 sm:h-4 ${classes.particleColor} rounded-full top-0 left-1/2 -ml-1.5 sm:-ml-2 shadow-lg`} />
                  </div>

                  {/* Ring 3 */}
                  <div
                    className={`absolute w-64 h-64 sm:w-80 sm:h-80 lg:w-[28rem] lg:h-[28rem] border-2 ${
                      theme === 'light' ? 'border-blue-300' : theme === 'dark' ? 'border-cyan-300' : 'border-purple-300'
                    } rounded-full opacity-15 animate-electron-orbit`}
                    style={{ animationDuration: '20s' }}
                  >
                    <div className={`absolute w-3 h-3 sm:w-4 sm:h-4 ${classes.particleColor} rounded-full top-0 left-1/2 -ml-1.5 sm:-ml-2 shadow-lg`} />
                  </div>
                </div>

                {/* Orbiting Electrons (Qubits) */}
                {[...Array(8)].map((_, i) => {
                  const angle = (i * 45);
                  return (
                    <div
                      key={`electron-${i}`}
                      className="absolute animate-orbit-1"
                      style={{
                        animationDelay: `${-i * 2.5}s`,
                        animationDuration: `${20 + i * 2}s`,
                        left: '50%',
                        top: '50%',
                        transform: `rotate(${angle}deg) translateX(80px) rotate(-${angle}deg)`,
                      }}
                    >
                      <div className={`w-3 h-3 sm:w-4 sm:h-4 ${classes.particleColor} rounded-full shadow-lg animate-pulse-glow`} />
                    </div>
                  );
                })}

                {/* Circuit Board Traces */}
                <svg className="absolute inset-0 w-full h-full opacity-20 pointer-events-none" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <linearGradient id="circuit-gradient">
                      <stop offset="0%" stopColor={theme === 'light' ? '#3b82f6' : theme === 'dark' ? '#06b6d4' : '#a855f7'} />
                      <stop offset="100%" stopColor={theme === 'light' ? '#8b5cf6' : theme === 'dark' ? '#3b82f6' : '#ec4899'} />
                    </linearGradient>
                  </defs>
                  <path
                    d="M 50 10 L 50 50 L 80 50 L 80 90"
                    stroke="url(#circuit-gradient)"
                    strokeWidth="2"
                    fill="none"
                    strokeDasharray="10 5"
                    className="animate-circuit-pulse"
                  />
                  <path
                    d="M 90 50 L 50 50 L 50 10"
                    stroke="url(#circuit-gradient)"
                    strokeWidth="2"
                    fill="none"
                    strokeDasharray="10 5"
                    className="animate-circuit-pulse"
                    style={{ animationDelay: '1s' }}
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className={`relative py-12 sm:py-20 ${theme === 'light' ? 'bg-white' : theme === 'dark' ? 'bg-slate-900' : 'bg-slate-950'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {stats.map((stat, index) => (
              <div
                key={index}
                className={`${classes.cardBg} backdrop-blur-md rounded-2xl p-6 sm:p-8 border ${classes.cardBorder} text-center card-3d animate-scale-in`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={`inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 rounded-full ${
                  theme === 'light'
                    ? 'bg-blue-100 text-blue-600'
                    : theme === 'dark'
                    ? 'bg-cyan-900/30 text-cyan-400'
                    : 'bg-purple-900/30 text-purple-400'
                } mb-4 animate-pulse-glow`}>
                  {stat.icon}
                </div>
                <div className={`text-3xl sm:text-4xl font-bold ${classes.cardText} mb-2`}>{stat.value}</div>
                <div className={`text-sm sm:text-base ${classes.cardSubtext}`}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className={`relative py-16 sm:py-24 ${classes.heroBg} overflow-hidden`}>
        {/* Background Circuit Pattern */}
        <div className="absolute inset-0 opacity-5">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <pattern id="circuit-pattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
              <path d="M 0 50 L 50 50 L 50 0" stroke="currentColor" strokeWidth="1" fill="none" />
              <circle cx="50" cy="50" r="3" fill="currentColor" />
            </pattern>
            <rect width="100%" height="100%" fill="url(#circuit-pattern)" />
          </svg>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16 animate-fade-in">
            <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-bold ${classes.heroText} mb-4`}>
              Core Technologies
            </h2>
            <p className={`text-base sm:text-lg ${classes.heroSubtext} max-w-3xl mx-auto`}>
              Advancing computational frontiers through integrated semiconductor, quantum, and superconducting innovations
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            {features.map((feature, index) => (
              <Link
                key={index}
                to={feature.link}
                className={`group ${classes.cardBg} backdrop-blur-md rounded-2xl p-6 sm:p-8 border ${classes.cardBorder} ${classes.cardHover} transition-all duration-300 card-3d animate-slide-in-bottom`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex flex-col h-full">
                  <div className={`inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-xl ${
                    theme === 'light'
                      ? 'bg-gradient-to-br from-blue-500 to-purple-500'
                      : theme === 'dark'
                      ? 'bg-gradient-to-br from-cyan-500 to-blue-500'
                      : 'bg-gradient-to-br from-purple-500 to-pink-500'
                  } text-white mb-4 sm:mb-6 group-hover:scale-110 transition-transform animate-pulse-glow`}>
                    {feature.icon}
                  </div>
                  
                  <h3 className={`text-xl sm:text-2xl font-bold ${classes.cardText} mb-3 sm:mb-4`}>
                    {feature.title}
                  </h3>
                  
                  <p className={`${classes.cardSubtext} mb-4 sm:mb-6 flex-grow text-sm sm:text-base`}>
                    {feature.description}
                  </p>
                  
                  <div className={`flex items-center ${
                    theme === 'light' ? 'text-blue-600' : theme === 'dark' ? 'text-cyan-400' : 'text-purple-400'
                  } font-semibold group-hover:translate-x-2 transition-transform`}>
                    Learn More
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className={`relative py-16 sm:py-24 ${theme === 'light' ? 'bg-white' : theme === 'dark' ? 'bg-slate-900' : 'bg-slate-950'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="space-y-6 sm:space-y-8 animate-slide-in-left">
              <div className="inline-block">
                <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${
                  theme === 'light'
                    ? 'bg-blue-100 text-blue-700'
                    : theme === 'dark'
                    ? 'bg-cyan-900/30 text-cyan-400'
                    : 'bg-purple-900/30 text-purple-400'
                } backdrop-blur-sm border ${classes.cardBorder}`}>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Our Vision
                </span>
              </div>

              <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-bold ${classes.heroText}`}>
                Shaping Tomorrow's Computing Landscape
              </h2>

              <p className={`text-base sm:text-lg ${classes.heroSubtext}`}>
                Becoming a globally recognized leader in quantum-enabled computing systems, empowering industries, 
                researchers, and institutions with accessible, reliable, and sustainable technologies.
              </p>

              <p className={`text-base sm:text-lg ${classes.heroSubtext}`}>
                We envision quantum and superconducting technologies seamlessly integrated into digital infrastructure, 
                driving breakthroughs in healthcare, finance, cybersecurity, climate modeling, and artificial intelligence.
              </p>

              <Link
                to="/about"
                className={`inline-flex items-center px-6 sm:px-8 py-3 sm:py-4 rounded-xl text-white font-semibold ${
                  theme === 'light'
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
                    : theme === 'dark'
                    ? 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700'
                    : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
                } transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1`}
              >
                About GLYQIX
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>

            <div className="grid grid-cols-2 gap-4 animate-slide-in-right">
              {['Quantum Computing', 'Superconducting Systems', 'Semiconductor Engineering', 'AI Integration'].map((tech, i) => (
                <div
                  key={i}
                  className={`${classes.cardBg} backdrop-blur-md rounded-xl p-4 sm:p-6 border ${classes.cardBorder} text-center card-3d animate-scale-in`}
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <div className={`text-2xl sm:text-3xl font-bold ${
                    theme === 'light'
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600'
                      : theme === 'dark'
                      ? 'bg-gradient-to-r from-cyan-400 to-blue-400'
                      : 'bg-gradient-to-r from-purple-400 to-pink-400'
                  } bg-clip-text text-transparent mb-2`}>
                    {(i + 1) * 25}%
                  </div>
                  <div className={`text-xs sm:text-sm ${classes.cardSubtext} font-medium`}>{tech}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={`relative py-16 sm:py-24 ${classes.heroBg} overflow-hidden`}>
        {/* Animated Background */}
        {theme === 'quantum' && (
          <div className="absolute inset-0">
            {[...Array(3)].map((_, i) => (
              <div
                key={`wave-${i}`}
                className="absolute inset-0 opacity-10 animate-magnetic-field"
                style={{
                  animationDelay: `${i * 2}s`,
                  background: `radial-gradient(circle at ${30 + i * 20}% ${40 + i * 10}%, rgba(168, 85, 247, 0.3), transparent 50%)`,
                }}
              />
            ))}
          </div>
        )}

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center animate-fade-in">
          <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-bold ${classes.heroText} mb-4 sm:mb-6`}>
            Join the Quantum Revolution
          </h2>
          <p className={`text-base sm:text-lg lg:text-xl ${classes.heroSubtext} mb-8 sm:mb-12 max-w-2xl mx-auto`}>
            Access cutting-edge resources, collaborate with leading researchers, and contribute to groundbreaking 
            innovations shaping the future of computing.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/signup"
              className={`inline-flex items-center justify-center px-8 py-4 rounded-xl text-white font-semibold ${
                theme === 'light'
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
                  : theme === 'dark'
                  ? 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700'
                  : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
              } transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1`}
            >
              Create Account
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link
              to="/careers"
              className={`inline-flex items-center justify-center px-8 py-4 rounded-xl font-semibold border-2 ${
                theme === 'light'
                  ? 'border-blue-600 text-blue-600 hover:bg-blue-50'
                  : theme === 'dark'
                  ? 'border-cyan-500 text-cyan-400 hover:bg-cyan-950'
                  : 'border-purple-500 text-purple-400 hover:bg-purple-950'
              } transition-all duration-300 backdrop-blur-sm`}
            >
              View Opportunities
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;